/**
 * The parts of an `.xlsx` package, as text (`docs/adr/9073`).
 *
 * An Excel workbook is a zip of XML parts. This module writes each of them: a row of a sheet, the
 * shared strings, the styles, the workbook, the relationships, the content types and the four parts
 * a header's notes take. It holds no zip and no library, so it can be imported without fflate and
 * tested without unzipping anything; `xlsx-package.ts` puts the parts in the zip, and `xlsx.ts`
 * decides what they say.
 *
 * **Why the parts are written by hand.** The writer this replaced (`write-excel-file`) built the
 * whole workbook, every cell an object and every part a string, in memory: 436,950 rows of sixty
 * columns would not fit in a browser tab. A sheet written row by row is a stream, and a stream
 * needs the parts to be independent — which they are, because nothing in a row refers to a part
 * written after it except by an index (a style, a shared string) that is allocated as the row is
 * written and published at the end. Every part's FORMAT is the one the library wrote, which is the
 * one Excel has been opening since DEC-98.
 *
 * **No formula from data, ever** (DEC-100, `docs/adr/9010`). A text cell is a shared-string cell
 * (`t="s"`): `=cmd|' /C calc'!A0` reaches the file as the characters it is. The only `<f>` this
 * module writes is the totals row's `SUBTOTAL(109,L2:L41)`, from a cell type that carries a range
 * `xlsx.ts` computed from a column letter and two row numbers.
 */

import type { XlsxCell, XlsxNote } from './xlsx';

/* eslint-disable bata/no-message-concatenation -- every template here assembles OOXML markup, which no person reads as a sentence and no catalogue holds (xlsx.ts's own notes are written by join for the same reason). */

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const SPREADSHEET_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const RELATIONSHIPS_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PACKAGE_RELATIONSHIPS_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const CONTENT_TYPES_NS = 'http://schemas.openxmlformats.org/package/2006/content-types';
const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

/** Excel's own limit on the rows of a sheet. */
export const EXCEL_MAX_ROWS = 1_048_576;

/**
 * A sheet would hold more rows than Excel opens. The one refusal the workbook keeps (owner ruling 2026-10-23):
 * a file Excel cannot open is worse than no file. Typed, so a screen can say so in words instead of «could not be produced».
 */
export class SheetTooLargeError extends RangeError {
  constructor(public readonly rows: number, public readonly limit: number = EXCEL_MAX_ROWS) {
    super(`a sheet holds at most ${limit} rows in Excel`);
    this.name = 'SheetTooLargeError';
  }
}

// ── text ─────────────────────────────────────────────────────────────────────

/** Characters XML 1.0 forbids (controls, lone surrogates, U+FFFE/F), U+FFFD, and the ones it discourages. */
// eslint-disable-next-line no-control-regex
const NOT_IN_XML = /[\0-\x08\x0B\f\x0E-\x1F\x7F-\x84\x86-\x9F\u{FDD0}-\u{FDEF}\u{FFFD}-\u{FFFF}]|[\u{D800}-\u{DBFF}](?![\u{DC00}-\u{DFFF}])|(?<![\u{D800}-\u{DBFF}])[\u{DC00}-\u{DFFF}]/u;
const NOT_IN_XML_ALL = new RegExp(NOT_IN_XML.source, 'gu');
const NEEDS_ESCAPE = /[&<>]/;

/**
 * Text content: what XML 1.0 cannot carry removed, the three characters that open markup escaped.
 * The common case — plain text — is one test and no copy.
 */
export function xmlContent(value: string): string {
  const clean = NOT_IN_XML.test(value) ? value.replace(NOT_IN_XML_ALL, '') : value;
  return NEEDS_ESCAPE.test(clean) ? clean.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') : clean;
}

/** An attribute value: {@link xmlContent}, and the two quotes. */
export function xmlAttribute(value: string): string {
  return xmlContent(value).replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/** XML text for a note and a sheet name: the five predefined entities, so a meaning with `<` or `&` stays itself. */
function xmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

const LETTERS: string[] = [];

/** A 0-based column index as Excel's letters. */
export function columnLetter(index: number): string {
  const known = LETTERS[index];
  if (known !== undefined) return known;
  let n = index + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  LETTERS[index] = letters;
  return letters;
}

// ── shared strings and styles ────────────────────────────────────────────────

/** More distinct strings than this are still written, but no longer looked up: a sentence unique to its row repeats nowhere. */
const MAX_DEDUPLICATED_STRINGS = 200_000;

/**
 * The workbook's shared strings, written as they are first met. A text that repeats (a store, a
 * country, an outcome) is one entry; the lookup stops growing at {@link MAX_DEDUPLICATED_STRINGS}
 * so a file of unique sentences costs the heap a bounded table, not one entry per row. The format
 * allows a string to appear twice, so a text that is no longer looked up is simply written again.
 */
export class SharedStrings {
  private readonly seen = new Map<string, number>();
  private pending: string[] = [];
  private pendingLength = 0;
  private count = 0;

  /** The index of `text`, adding it when it is new. */
  index(text: string): number {
    const known = this.seen.get(text);
    if (known !== undefined) return known;
    const id = this.count;
    this.count += 1;
    if (this.seen.size < MAX_DEDUPLICATED_STRINGS) this.seen.set(text, id);
    const preserve = text.trim().length === text.length ? '' : ' xml:space="preserve"';
    const xml = `<si><t${preserve}>${xmlContent(text)}</t></si>`;
    this.pending.push(xml);
    this.pendingLength += xml.length;
    return id;
  }

  /** Characters waiting to be written. */
  get waiting(): number { return this.pendingLength; }

  /** The entries added since the last call. */
  take(): string {
    const xml = this.pending.join('');
    this.pending = [];
    this.pendingLength = 0;
    return xml;
  }

  static readonly START = `<?xml version="1.0"?><sst xmlns="${SPREADSHEET_NS}">`;
  static readonly END = '</sst>';
}

/** Excel's "Text" number format: a cell typed over later stays text too. Built in, as number format 49. */
export const TEXT_FORMAT = '@';
const TEXT_FORMAT_ID = 49;
/** Custom number formats are numbered from here (Excel's own convention). */
const FIRST_CUSTOM_FORMAT_ID = 164;

/**
 * The styles the cells use, numbered as they are first asked for: a number format, bold or not.
 * Style 0 is the default. The `styles.xml` part is written last, when every style a cell took is known.
 */
export class StyleBook {
  private readonly ids = new Map<string, readonly [number, number]>();
  private readonly formats: string[] = [];
  private readonly xfs: string[] = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>'];

  /** The style index for a number format (`undefined`: none) in regular or bold type. */
  id(format: string | undefined, bold: boolean): number {
    const key = format ?? '';
    let pair = this.ids.get(key);
    if (pair === undefined) {
      pair = [this.add(format, false), this.add(format, true)];
      this.ids.set(key, pair);
    }
    return bold ? pair[1] : pair[0];
  }

  private add(format: string | undefined, bold: boolean): number {
    if (format === undefined && !bold) return 0;
    let formatId = 0;
    if (format === TEXT_FORMAT) formatId = TEXT_FORMAT_ID;
    else if (format !== undefined) {
      let at = this.formats.indexOf(format);
      if (at === -1) { at = this.formats.length; this.formats.push(format); }
      formatId = FIRST_CUSTOM_FORMAT_ID + at;
    }
    const attributes = [
      `numFmtId="${formatId}"`, `fontId="${bold ? 1 : 0}"`, 'fillId="0"', 'borderId="0"',
      ...(formatId === 0 ? [] : ['applyNumberFormat="1"']), ...(bold ? ['applyFont="1"'] : []),
    ];
    this.xfs.push(`<xf ${attributes.join(' ')}/>`);
    return this.xfs.length - 1;
  }

  /** `xl/styles.xml`. */
  xml(): string {
    const numFmts = this.formats.length === 0 ? '' : `<numFmts count="${this.formats.length}">${
      this.formats.map((code, i) => `<numFmt numFmtId="${FIRST_CUSTOM_FORMAT_ID + i}" formatCode="${xmlAttribute(code)}"/>`).join('')
    }</numFmts>`;
    return `<?xml version="1.0" ?><styleSheet xmlns="${SPREADSHEET_NS}">${numFmts}`
      + '<fonts count="2"><font><family val="2"/><scheme val="minor"/><color theme="1"/></font>'
      + '<font><family val="2"/><scheme val="minor"/><b/><color theme="1"/></font></fonts>'
      + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
      + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
      + `<cellXfs count="${this.xfs.length}">${this.xfs.join('')}</cellXfs></styleSheet>`;
  }
}

// ── cells and rows ───────────────────────────────────────────────────────────

/** Days from Excel's 1900 epoch to the Unix epoch, leap years counted. */
const DAYS_BEFORE_UNIX_EPOCH = 70 * 365 + 19;
const DAY_MS = 24 * 60 * 60 * 1000;

/** An instant as Excel's serial date: days since its epoch, the time of day a fraction. */
export function serialDate(date: Date): number {
  return date.getTime() / DAY_MS + DAYS_BEFORE_UNIX_EPOCH;
}

function styleAttribute(style: number): string {
  return style === 0 ? '' : ` s="${style}"`;
}

function textCell(ref: string, text: string, bold: boolean, styles: StyleBook, strings: SharedStrings): string {
  return `<c r="${ref}"${styleAttribute(styles.id(TEXT_FORMAT, bold))} t="s"><v>${strings.index(text)}</v></c>`;
}

/** One cell as a `<c>` element; a blank cell is no element at all. */
export function cellXml(ref: string, cell: XlsxCell, bold: boolean, styles: StyleBook, strings: SharedStrings): string {
  switch (cell.type) {
    case 'blank': return '';
    case 'text': return textCell(ref, cell.value, bold, styles, strings);
    case 'number':
      // A number Excel cannot hold is the text it prints, as the layout does for an identifier.
      if (!Number.isFinite(cell.value)) return textCell(ref, String(cell.value), bold, styles, strings);
      return `<c r="${ref}"${styleAttribute(styles.id(cell.format, bold))}><v>${cell.value}</v></c>`;
    case 'date':
      return `<c r="${ref}"${styleAttribute(styles.id(cell.format, bold))}><v>${serialDate(cell.value)}</v></c>`;
    // The range is `xlsx.ts`'s own (a column letter and two row numbers), never a string a builder supplied.
    case 'subtotal':
      return `<c r="${ref}"${styleAttribute(styles.id(cell.format, bold))}><f>${xmlContent(`SUBTOTAL(109,${cell.range})`)}</f></c>`;
    default: { const exhaustive: never = cell; throw new Error(String(exhaustive)); }
  }
}

/** One row. `rowNumber` is Excel's, 1-based. */
export function rowXml(rowNumber: number, cells: readonly XlsxCell[], bold: boolean, styles: StyleBook, strings: SharedStrings): string {
  if (rowNumber > EXCEL_MAX_ROWS) throw new SheetTooLargeError(rowNumber);
  let body = '';
  for (let i = 0; i < cells.length; i += 1) body += cellXml(`${columnLetter(i)}${rowNumber}`, cells[i]!, bold, styles, strings);
  return `<row r="${rowNumber}">${body}</row>`;
}

/** The header row: bold text, no number format — the row the pane freezes. */
export function headerRowXml(header: readonly string[], styles: StyleBook, strings: SharedStrings): string {
  const style = styleAttribute(styles.id(undefined, true));
  let body = '';
  for (let i = 0; i < header.length; i += 1) body += `<c r="${columnLetter(i)}1"${style} t="s"><v>${strings.index(header[i]!)}</v></c>`;
  return `<row r="1">${body}</row>`;
}

// ── a sheet ──────────────────────────────────────────────────────────────────

/** The relationship ids a sheet's notes take. */
export const NOTE_RELS = { vml: 'rIdNoteVml', comments: 'rIdNoteComments' } as const;

export interface SheetHead {
  readonly widths: readonly number[];
  readonly frozenColumns: number;
  /** The first sheet is the one the workbook opens on. */
  readonly selected: boolean;
}

/** Everything of a worksheet before its first row: the frozen pane, the column widths, `<sheetData>`. */
export function sheetHeadXml(head: SheetHead): string {
  // The header row is always frozen, the leading columns with it. Excel's own form of a pane.
  const pane = head.frozenColumns > 0
    ? `<pane xSplit="${head.frozenColumns}" ySplit="1" topLeftCell="${columnLetter(head.frozenColumns)}2" activePane="bottomRight" state="frozen"/>`
    : '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>';
  const cols = head.widths.length === 0 ? '' : `<cols>${head.widths.map((width, i) => `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`).join('')}</cols>`;
  return `<?xml version="1.0" ?><worksheet xmlns="${SPREADSHEET_NS}" xmlns:r="${RELATIONSHIPS_NS}">`
    + `<sheetViews><sheetView${head.selected ? ' tabSelected="1"' : ''} workbookViewId="0">${pane}</sheetView></sheetViews>${cols}<sheetData>`;
}

/**
 * Everything of a worksheet after its last row: the autofilter, then the note drawing — a worksheet's
 * children are a sequence, not a set, and `legacyDrawing` closes it. Out of order, Excel calls the file
 * unreadable rather than ignoring what it did not expect.
 */
export function sheetTailXml(autoFilter: string | null, hasNotes: boolean): string {
  const filter = autoFilter === null ? '' : ['<autoFilter ref="', autoFilter, '"/>'].join('');
  const legacy = hasNotes ? `<legacyDrawing r:id="${NOTE_RELS.vml}"/>` : '';
  return `</sheetData>${filter}${legacy}</worksheet>`;
}

// ── the header's notes, as OOXML ─────────────────────────────────────────────

/**
 * An Excel NOTE is four parts, and the format is old enough that three of them are legacy:
 *
 *   `xl/comments{n}.xml`              the text, per cell, in SpreadsheetML
 *   `xl/drawings/vmlDrawing{n}.vml`   the BOX each note is drawn in — VML, the pre-2007 vector
 *                                     format Excel still requires here and nowhere else
 *   the sheet's `.rels`               one relationship to each of those two parts
 *   `[Content_Types].xml`             the `vml` default and one override per comments part
 *
 * and then `<legacyDrawing r:id="…"/>` in the sheet itself ({@link sheetTailXml}).
 */

/**
 * The background Excel itself paints a note: `#ffffe1`. Not a design token and never one — it
 * belongs to the file format, not to this product's palette, and a note in any other colour is a
 * note that does not look like a note. Named once so `no-colour-literal` has one place to allow.
 */
// eslint-disable-next-line bata/no-colour-literal -- Excel's own note background, part of the format
const NOTE_FILL = '#ffffe1';

/** The note's box, in points. Wide enough to read, tall enough for the text it holds. */
function noteBox(text: string): { readonly width: number; readonly height: number } {
  const width = 340;
  // ~52 characters a line at this width and font; two lines of margin for the bold header.
  const lines = Math.ceil(text.length / 52) + 2;
  return { width, height: Math.min(460, Math.max(70, lines * 13)) };
}

/** What a sheet's notes are written from. */
export interface NotedSheet {
  readonly header: readonly string[];
  readonly notes: readonly XlsxNote[];
}

/** `xl/comments{n}.xml` — the text of every note on one sheet. */
export function commentsXml(sheet: NotedSheet): string {
  const comments = sheet.notes.map((note) => {
    const head = sheet.header[note.at] ?? '';
    const font = '<rPr><sz val="9"/><color indexed="81"/><rFont val="Tahoma"/><family val="2"/></rPr>';
    // The column's name bold, then its meaning: the box can float away from the cell it belongs to.
    const title = `<r><rPr><b/><sz val="9"/><color indexed="81"/><rFont val="Tahoma"/><family val="2"/></rPr><t>${xmlText(head)}</t></r>`;
    const body = `<r>${font}<t xml:space="preserve">
${xmlText(note.text)}</t></r>`;
    // Assembled by join, not interpolation: this is OOXML markup, and `no-message-concatenation`
    // guards sentences a person reads.
    return ['<comment ref="', columnLetter(note.at), '1" authorId="0"><text>', title, body, '</text></comment>'].join('');
  });
  // One author, empty: Excel prefixes the note with the author's name, and this note has no author
  // — it is the catalogue speaking, not a person.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<comments xmlns="${SPREADSHEET_NS}">`
    + `<authors><author></author></authors><commentList>${comments.join('')}</commentList></comments>`;
}

/** `xl/drawings/vmlDrawing{n}.vml` — one hidden box per note, anchored beside its header cell. */
export function vmlDrawingXml(sheet: NotedSheet, block: number): string {
  const shapes = sheet.notes.map((note, i) => {
    const { width, height } = noteBox(note.text);
    // The anchor is (col, dx, row, dy) twice: where the box starts and where it ends. It opens one
    // column to the right of the header and spans four, below the header row.
    const anchor = `${note.at + 1}, 15, 0, 2, ${note.at + 5}, 15, ${Math.max(3, Math.round(height / 20))}, 4`;
    return ['<v:shape id="_x0000_s', String(block * 1024 + i + 1), '" type="#_x0000_t202"',
      ' style="position:absolute;width:', String(width), 'pt;height:', String(height),
      'pt;z-index:', String(i + 1), ';visibility:hidden"',
      ' fillcolor="', NOTE_FILL, '" o:insetmode="auto">',
      '<v:fill color2="', NOTE_FILL, '"/><v:shadow on="t" color="black" obscured="t"/>'].join('')
      + `<v:path o:connecttype="none"/><v:textbox style="mso-direction-alt:auto"><div style="text-align:left"/></v:textbox>`
      + `<x:ClientData ObjectType="Note"><x:MoveWithCells/><x:SizeWithCells/>`
      + `<x:Anchor>${anchor}</x:Anchor><x:AutoFill>False</x:AutoFill>`
      + `<x:Row>0</x:Row><x:Column>${note.at}</x:Column></x:ClientData></v:shape>`;
  });
  return `<xml xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office"`
    + ` xmlns:x="urn:schemas-microsoft-com:office:excel">`
    + `<o:shapelayout v:ext="edit"><o:idmap v:ext="edit" data="${block}"/></o:shapelayout>`
    + `<v:shapetype id="_x0000_t202" coordsize="21600,21600" o:spt="202" path="m,l,21600r21600,l21600,xe">`
    + `<v:stroke joinstyle="miter"/><v:path gradientshapeok="t" o:connecttype="rect"/></v:shapetype>`
    + `${shapes.join('')}</xml>`;
}

/** `xl/worksheets/_rels/sheet{n}.xml.rels` of a sheet with notes: the two parts it draws them from. */
export function sheetRelsXml(n: number): string {
  const rel = (id: string, type: string, target: string): string => [
    '<Relationship Id="', id,
    '" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/', type,
    '" Target="', target, '"/>',
  ].join('');
  return `${XML_DECLARATION}<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NS}">`
    + rel(NOTE_RELS.vml, 'vmlDrawing', `../drawings/vmlDrawing${n}.vml`)
    + rel(NOTE_RELS.comments, 'comments', `../comments${n}.xml`)
    + '</Relationships>';
}

// ── the package ──────────────────────────────────────────────────────────────

/** A sheet name in a formula reference: quoted, an apostrophe doubled, XML-escaped. */
const quotedSheet = (name: string): string => `'${name.replace(/'/g, "''")}'`
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** `$A$1:$F$41` for `A1:F41` — how Excel records a filter's range in its hidden name. */
const absolute = (range: string): string => range.replace(/([A-Z]+)(\d+)/g, '$$$1$$$2');

export interface PackageSheet {
  readonly name: string;
  readonly autoFilter: string | null;
  readonly hasNotes: boolean;
}

/** `[Content_Types].xml`. */
export function contentTypesXml(sheets: readonly PackageSheet[]): string {
  const override = (part: string, type: string): string => `<Override ContentType="${type}" PartName="${part}"/>`;
  const sheetType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml';
  const commentsType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.comments+xml';
  const noted = sheets.flatMap((sheet, index) => (sheet.hasNotes ? [index + 1] : []));
  return `${XML_DECLARATION}<Types xmlns="${CONTENT_TYPES_NS}">`
    + '<Default ContentType="application/xml" Extension="xml"/>'
    + '<Default ContentType="application/vnd.openxmlformats-package.relationships+xml" Extension="rels"/>'
    + (noted.length === 0 ? '' : '<Default Extension="vml" ContentType="application/vnd.openxmlformats-officedocument.vmlDrawing"/>')
    + override('/xl/workbook.xml', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml')
    + override('/xl/sharedStrings.xml', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sharedStrings+xml')
    + override('/xl/styles.xml', 'application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml')
    + sheets.map((_, i) => override(`/xl/worksheets/sheet${i + 1}.xml`, sheetType)).join('')
    + noted.map((n) => override(`/xl/comments${n}.xml`, commentsType)).join('')
    + '</Types>';
}

/** `_rels/.rels`. */
export function packageRelsXml(): string {
  return `${XML_DECLARATION}<Relationships xmlns="${PACKAGE_RELATIONSHIPS_NS}">`
    + '<Relationship Id="rId-workbook-1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
    + '</Relationships>';
}

/** `xl/_rels/workbook.xml.rels`. */
export function workbookRelsXml(sheetCount: number): string {
  const rel = (id: number, type: string, target: string): string => (
    `<Relationship Id="rId${id}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/${type}" Target="${target}"/>`
  );
  const sheets = Array.from({ length: sheetCount }, (_, i) => rel(i + 1, 'worksheet', `worksheets/sheet${i + 1}.xml`));
  return `<?xml version="1.0" ?><Relationships xmlns="${PACKAGE_RELATIONSHIPS_NS}">${sheets.join('')}`
    + `${rel(sheetCount + 1, 'sharedStrings', 'sharedStrings.xml')}${rel(sheetCount + 2, 'styles', 'styles.xml')}</Relationships>`;
}

/**
 * `xl/workbook.xml`: the sheets, each data sheet's autofilter under Excel's hidden `_FilterDatabase`
 * name (which is how Excel itself records one), and `fullCalcOnLoad` so the totals row's `SUBTOTAL` is
 * computed when the file opens rather than shown empty.
 */
export function workbookXml(sheets: readonly PackageSheet[]): string {
  const names = sheets.flatMap((sheet, index) => (sheet.autoFilter === null ? [] : [
    `<definedName name="_xlnm._FilterDatabase" localSheetId="${index}" hidden="1">${quotedSheet(sheet.name)}!${absolute(sheet.autoFilter)}</definedName>`,
  ]));
  return `${XML_DECLARATION}<workbook xmlns="${SPREADSHEET_NS}" xmlns:r="${RELATIONSHIPS_NS}">`
    + '<workbookPr/><bookViews><workbookView/></bookViews>'
    + `<sheets>${sheets.map((sheet, i) => `<sheet r:id="rId${i + 1}" sheetId="${i + 1}" name="${xmlAttribute(sheet.name)}"/>`).join('')}</sheets>`
    + `${names.length === 0 ? '' : `<definedNames>${names.join('')}</definedNames>`}<calcPr fullCalcOnLoad="1"/></workbook>`;
}
